import { useMemo, useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Plus, Route } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/ui/data-table';
import { FetchErrorCallout } from '@/components/ui/fetch-error-callout';
import { PageHeader } from '@/components/shell/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { ConfirmDeleteDialog } from '@/features/patient-record/components/_shared/ConfirmDeleteDialog';
import {
  isDeleteBlockedByEnrollments,
  useAdminCarePathwayTemplateMutations,
  useAdminCarePathwayTemplates,
  type TemplateInput,
} from '@/features/admin-care-pathways/hooks';
import { AdminCarePathwayTemplatesTable } from '@/features/admin-care-pathways/components/AdminCarePathwayTemplatesTable';
import { TemplateEditorDialog } from '@/features/admin-care-pathways/components/TemplateEditorDialog';
import { tenantQueries } from '@/features/patient-record/queries';
import type { CarePathwayTemplateViewModel } from '@/features/patient-record/types';

/**
 * Admin / Care Pathways template catalogue (PTV2-1174 Part B).
 *
 * Renders every template (incl. DISABLED so admins can re-enable). Create
 * + edit live in a shared `TemplateEditorDialog`; disable/re-enable is a
 * single-click action; delete is enrollment-aware: blocked from the row
 * menu when `enrollment_count > 0`, and falls back to a "disable instead"
 * message if the BE returns 409 anyway.
 */
export function AdminCarePathwaysPage(): ReactElement {
  const { t } = useTranslation();
  const query = useAdminCarePathwayTemplates();
  const { create, update, setState, remove } = useAdminCarePathwayTemplateMutations();

  const [editorOpen, setEditorOpen] = useState<
    null | { kind: 'create' } | { kind: 'edit'; templateId: string }
  >(null);

  // The list endpoint may omit the full `steps` array, so fetch the
  // single-template detail when the editor opens. Falls back to the list
  // row's data while the detail fetch is in flight.
  const editTemplateDetail = useQuery({
    ...tenantQueries.carePathwayTemplate(
      editorOpen?.kind === 'edit' ? editorOpen.templateId : '',
    ),
    enabled: editorOpen?.kind === 'edit',
  });
  const [pendingDelete, setPendingDelete] = useState<CarePathwayTemplateViewModel | null>(null);
  const [deleteBlockedMessage, setDeleteBlockedMessage] = useState<string | null>(null);

  const rows = useMemo(
    () => (query.isSuccess ? [...query.data].sort((a, b) => a.name.localeCompare(b.name)) : []),
    [query.isSuccess, query.data],
  );

  const editorBusy = create.isPending || update.isPending;
  const editorError =
    editorOpen?.kind === 'create' && create.isError
      ? t('routes.admin.carePathways.errorSave')
      : editorOpen?.kind === 'edit' && update.isError
        ? t('routes.admin.carePathways.errorSave')
        : null;

  const handleEditorSubmit = (input: TemplateInput): void => {
    if (editorOpen === null) return;
    if (editorOpen.kind === 'create') {
      create.mutate(input, {
        onSuccess: () => {
          setEditorOpen(null);
        },
      });
    } else {
      update.mutate(
        { id: editorOpen.templateId, input },
        {
          onSuccess: () => {
            setEditorOpen(null);
          },
        },
      );
    }
  };

  const busyId = setState.isPending
    ? setState.variables.id
    : remove.isPending
      ? remove.variables
      : null;

  return (
    <>
      <PageHeader
        icon={<Route className="h-[18px] w-[18px]" aria-hidden="true" />}
        title={t('routes.admin.carePathways.heading')}
        subtitle={t('routes.admin.carePathways.subheading')}
      />
      <div className="px-8 py-5 space-y-4 bg-background">
        <section aria-labelledby="admin-care-pathways-heading" className="space-y-4">
          <header className="flex items-start justify-between gap-4">
            <div>
              <h2
                id="admin-care-pathways-heading"
                className="text-base font-semibold text-foreground"
              >
                {t('routes.admin.carePathways.tableHeading')}
              </h2>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('routes.admin.carePathways.tableSubheading')}
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                create.reset();
                setEditorOpen({ kind: 'create' });
              }}
            >
              <Plus className="h-4 w-4" aria-hidden="true" />
              {t('routes.admin.carePathways.createButton')}
            </Button>
          </header>

          {query.isPending && <RowsSkeleton />}

          {query.isError && (
            <div className="max-w-xl">
              <FetchErrorCallout
                error={query.error}
                heading={t('routes.admin.carePathways.error.heading')}
                message={t('routes.admin.carePathways.error.message')}
                codeLabel={t('routes.admin.carePathways.error.codeLabel')}
                onRetry={() => {
                  query.refetch().catch(() => undefined);
                }}
                retryLabel={t('routes.admin.carePathways.error.retry')}
              />
            </div>
          )}

          {query.isSuccess && rows.length === 0 && (
            <p role="status" className="text-sm text-muted-foreground">
              {t('routes.admin.carePathways.empty')}
            </p>
          )}

          {query.isSuccess && rows.length > 0 && (
            <DataTable aria-label={t('routes.admin.carePathways.tableRegion')}>
              <AdminCarePathwayTemplatesTable
                templates={rows}
                busyId={busyId}
                onEdit={(tpl) => {
                  update.reset();
                  setEditorOpen({ kind: 'edit', templateId: tpl.id });
                }}
                onToggleState={(tpl) => {
                  setState.mutate({
                    id: tpl.id,
                    state: tpl.state === 'ACTIVE' ? 'DISABLED' : 'ACTIVE',
                  });
                }}
                onDelete={(tpl) => {
                  setDeleteBlockedMessage(null);
                  remove.reset();
                  setPendingDelete(tpl);
                }}
              />
            </DataTable>
          )}
        </section>
      </div>

      {editorOpen?.kind === 'create' && (
        <TemplateEditorDialog
          mode="create"
          initial={null}
          busy={editorBusy}
          errorMessage={editorError}
          onCancel={() => {
            setEditorOpen(null);
          }}
          onSubmit={handleEditorSubmit}
        />
      )}
      {/*
        Edit mounts only after the single-template detail fetch resolves —
        the list endpoint may omit the full steps array, so opening the
        editor with the row's data alone risks dropping a template's steps
        on save. `TemplateEditorDialog` snapshots `initial` on mount, so
        we wait for the full payload before rendering it.
      */}
      {editorOpen?.kind === 'edit' && editTemplateDetail.isSuccess && (
        <TemplateEditorDialog
          mode="edit"
          initial={editTemplateDetail.data}
          busy={editorBusy}
          errorMessage={editorError}
          onCancel={() => {
            setEditorOpen(null);
          }}
          onSubmit={handleEditorSubmit}
        />
      )}

      {pendingDelete !== null && (
        <ConfirmDeleteDialog
          title={t('routes.admin.carePathways.deleteTitle')}
          message={
            deleteBlockedMessage ??
            t('routes.admin.carePathways.deleteMessage', { name: pendingDelete.name })
          }
          confirmLabel={t('routes.admin.carePathways.deleteConfirm')}
          busy={remove.isPending}
          onCancel={() => {
            setPendingDelete(null);
            setDeleteBlockedMessage(null);
          }}
          onConfirm={() => {
            remove.mutate(pendingDelete.id, {
              onSuccess: () => {
                setPendingDelete(null);
                setDeleteBlockedMessage(null);
              },
              onError: (err) => {
                if (isDeleteBlockedByEnrollments(err)) {
                  setDeleteBlockedMessage(
                    t('routes.admin.carePathways.deleteBlockedMessage', {
                      count: pendingDelete.enrollmentCount,
                    }),
                  );
                } else {
                  setDeleteBlockedMessage(t('routes.admin.carePathways.errorDelete'));
                }
              },
            });
          }}
        />
      )}
    </>
  );
}

function RowsSkeleton(): ReactElement {
  return (
    <div className="space-y-2" aria-busy="true">
      {Array.from({ length: 4 }, (_, i) => (
        <Skeleton key={i} className="h-9 w-full" />
      ))}
    </div>
  );
}
